import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Plus, PencilSimple, CircleNotch, Copy, CheckCircle, UserGear, CaretLeft, Key, ClockCounterClockwise, SignIn, ArrowSquareOut } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";
import { supabase } from "../lib/supabase";
import api from "../lib/api";

const emptyForm = { email: "", subscription: null, archive_package: null, fascicolo: false, archive_storage_gb: "", dossier_quota_gb: "", dossier_max_file_mb: "", note: "" };
const fmtGb = (b) => ((b || 0) / 1073741824).toFixed((b || 0) >= 107374182 ? 1 : 2);

export const AdminPanel = ({ onClose, embedded = false }) => {
  const { t } = useLang();
  const a = t.admin;
  const [clients, setClients] = useState(null);
  const [view, setView] = useState("list");
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [logs, setLogs] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = () => api.get("/admin/clients").then(({ data }) => setClients(data.items)).catch(() => toast.error(a.error));
  useEffect(() => { load(); }, []);

  const openEdit = (c) => {
    setForm({
      email: c.email,
      subscription: c.subscription_active ? c.subscription || "monthly" : null,
      archive_package: c.archive_package || null,
      fascicolo: !!c.fascicolo_package,
      archive_storage_gb: c.archive_storage_limit ? c.archive_storage_limit / 1073741824 : "",
      dossier_quota_gb: c.dossier_quota ? c.dossier_quota / 1073741824 : "",
      dossier_max_file_mb: c.dossier_max_file ? c.dossier_max_file / 1048576 : "",
      note: "",
    });
    setLogs(null);
    api.get(`/admin/clients/${encodeURIComponent(c.email)}/logs`).then(({ data }) => setLogs(data.items)).catch(() => setLogs([]));
    setView("edit");
  };

  const payload = () => ({
    subscription: form.subscription,
    archive_package: form.archive_package,
    fascicolo: form.fascicolo,
    archive_storage_gb: form.archive_package && form.archive_storage_gb ? Number(form.archive_storage_gb) : null,
    dossier_quota_gb: form.fascicolo && form.dossier_quota_gb ? Number(form.dossier_quota_gb) : null,
    dossier_max_file_mb: form.fascicolo && form.dossier_max_file_mb ? Number(form.dossier_max_file_mb) : null,
    note: form.note?.trim() || null,
  });

  const resetPwd = async () => {
    if (!window.confirm(a.resetConfirm)) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/clients/${encodeURIComponent(form.email)}/reset-password`);
      setCreated({ ...data, reset: true });
      setView("password");
    } catch (err) {
      toast.error(a.error);
    } finally {
      setResetting(false);
    }
  };

  const impersonate = async (c) => {
    if (!window.confirm(a.impersonateConfirm.replace("{email}", c.email))) return;
    try {
      const { data } = await api.post(`/admin/clients/${encodeURIComponent(c.email)}/impersonate`);
      const { error } = await supabase.auth.verifyOtp({ type: "magiclink", token_hash: data.token_hash });
      if (error) throw error;
      sessionStorage.setItem("cqr_impersonating", c.email);
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err?.response?.data?.detail === "auth_user_not_found" ? a.impersonateNoUser : a.error);
    }
  };

  const errText = (e) => {
    const d = e?.response?.data?.detail;
    if (d === "client_exists") return a.errExists;
    if (d === "invalid_email") return a.errEmail;
    if (d === "invalid_override") return a.errOverride;
    return a.error;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (view === "create") {
        const { data } = await api.post("/admin/clients", { email: form.email, ...payload() });
        if (data.existing_user) {
          toast.success(a.createdExisting);
          setView("list");
        } else {
          setCreated(data);
          setView("password");
        }
      } else {
        await api.put(`/admin/clients/${encodeURIComponent(form.email)}`, payload());
        toast.success(a.saved);
        setView("list");
      }
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const copyPwd = () => {
    navigator.clipboard.writeText(created.temporary_password);
    toast.success(a.copied);
  };

  const check = (checked, onChange, label, testid) => (
    <label className={`flex items-center gap-3 rounded-md border px-4 py-3 text-sm cursor-pointer transition ${checked ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
      <input type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4 accent-emerald-500" data-testid={testid} />
      <span className="font-medium">{label}</span>
    </label>
  );

  const numField = (key, label, min, testid) => (
    <div>
      <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{label}</label>
      <input type="number" min={min} step="any" value={form[key]} placeholder={String(min)}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full rounded-md border border-slate-300 px-3.5 py-2.5 text-sm text-[#0F172A] outline-none focus:border-emerald-500"
        data-testid={testid} />
    </div>
  );

  const badges = (c) => {
    const items = [];
    if (c.subscription_active) items.push(["sub", c.subscription === "annual" ? a.badgeAnnual : a.badgeMonthly, "bg-emerald-100 text-emerald-700"]);
    if (c.archive_package) items.push(["arc", `${a.badgeArchive} ${c.archive_package === "pro" ? "Pro" : "Base"}`, "bg-slate-200 text-slate-700"]);
    if (c.fascicolo_package) items.push(["fas", "Fascicolo Pro", "bg-indigo-100 text-indigo-700"]);
    if (!items.length) items.push(["none", a.none, "bg-amber-100 text-amber-700"]);
    return items.map(([k, label, cls]) => (
      <span key={k} className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${cls}`}>{label}</span>
    ));
  };

  const overrides = (c) => {
    const parts = [];
    if (c.archive_storage_limit) parts.push(`${a.ovShortArchive} ${fmtGb(c.archive_storage_limit)} GB`);
    if (c.dossier_quota) parts.push(`${a.ovShortQuota} ${fmtGb(c.dossier_quota)} GB`);
    if (c.dossier_max_file) parts.push(`${a.ovShortFile} ${Math.round(c.dossier_max_file / 1048576)} MB`);
    return parts.join(" · ");
  };

  const fieldLabels = {
    subscription: a.fSubscription, archive_package: a.fArchive, fascicolo_package: a.fFascicolo,
    archive_storage_limit: a.fStorage, dossier_quota: a.fQuota, dossier_max_file: a.fMaxFile,
  };
  const fmtVal = (k, v) => {
    if (v === null || v === undefined || v === false) return "—";
    if (k === "archive_storage_limit" || k === "dossier_quota") return `${fmtGb(v)} GB`;
    if (k === "dossier_max_file") return `${Math.round(v / 1048576)} MB`;
    return String(v);
  };
  const actionLabel = (act) => (act === "create" ? a.logCreate : act === "password_reset" ? a.logReset : act === "impersonate" ? a.logImpersonate : a.logUpdate);

  const logsSection = (
    <div className="px-6 pb-6">
      <div className="flex items-center gap-2 border-t border-slate-200 pt-5 mb-3">
        <ClockCounterClockwise size={16} className="text-slate-400" />
        <p className="font-mono-tech text-[11px] uppercase tracking-wider text-slate-500">{a.history}</p>
      </div>
      {!logs ? (
        <div className="flex justify-center py-6"><CircleNotch size={20} className="text-emerald-500 animate-spin" /></div>
      ) : logs.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="admin-logs-empty">{a.historyEmpty}</p>
      ) : (
        <ul className="space-y-3" data-testid="admin-logs-list">
          {logs.map((l, i) => (
            <li key={l.id} className="rounded-md border border-slate-100 bg-slate-50/60 px-4 py-3" data-testid={`admin-log-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${l.action === "password_reset" ? "bg-amber-100 text-amber-700" : l.action === "impersonate" ? "bg-indigo-100 text-indigo-700" : l.action === "create" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>{actionLabel(l.action)}</span>
                <span className="text-[11px] text-slate-400 font-mono-tech">{new Date(l.created_at).toLocaleString()} · {a.by} {l.admin_email}</span>
              </div>
              {Object.keys(l.changes || {}).length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {Object.entries(l.changes).map(([k, ch]) => (
                    <li key={k} className="text-xs text-slate-600">
                      <span className="font-semibold">{fieldLabels[k] || k}:</span> {fmtVal(k, ch.from)} → <span className="text-emerald-700 font-semibold">{fmtVal(k, ch.to)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {l.note && <p className="mt-1.5 text-xs italic text-slate-500">"{l.note}"</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const formView = (
    <form onSubmit={submit} className="px-6 py-6 space-y-5">
      {view === "create" ? (
        <div>
          <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{a.email}</label>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full rounded-md border border-slate-300 px-3.5 py-2.5 text-sm text-[#0F172A] outline-none focus:border-emerald-500"
            data-testid="admin-client-email-input" />
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <p className="font-mono-tech text-sm font-semibold text-[#0F172A] truncate" data-testid="admin-edit-email">{form.email}</p>
          <button type="button" onClick={resetPwd} disabled={resetting}
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3.5 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-60 shrink-0"
            data-testid="admin-reset-password-btn">
            <Key size={14} /> {resetting ? a.creating : a.resetPwd}
          </button>
        </div>
      )}
      <div>
        <p className="font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-2">{a.packages}</p>
        <div className="space-y-2">
          {check(form.subscription === "monthly", () => setForm({ ...form, subscription: form.subscription === "monthly" ? null : "monthly" }), a.pkgMonthly, "admin-pkg-monthly")}
          {check(form.subscription === "annual", () => setForm({ ...form, subscription: form.subscription === "annual" ? null : "annual" }), a.pkgAnnual, "admin-pkg-annual")}
          {check(form.archive_package === "base", () => setForm({ ...form, archive_package: form.archive_package === "base" ? null : "base" }), a.pkgArchiveBase, "admin-pkg-archive-base")}
          {check(form.archive_package === "pro", () => setForm({ ...form, archive_package: form.archive_package === "pro" ? null : "pro" }), a.pkgArchivePro, "admin-pkg-archive-pro")}
          {check(form.fascicolo, () => setForm({ ...form, fascicolo: !form.fascicolo }), a.pkgFascicolo, "admin-pkg-fascicolo")}
        </div>
      </div>
      {(form.archive_package || form.fascicolo) && (
        <div>
          <p className="font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-1">{a.overrides}</p>
          <p className="text-xs text-slate-400 mb-3">{a.ovHint}</p>
          <div className="space-y-3">
            {form.archive_package && numField("archive_storage_gb", a.ovArchive, 2, "admin-ov-archive-input")}
            {form.fascicolo && numField("dossier_quota_gb", a.ovQuota, 5, "admin-ov-quota-input")}
            {form.fascicolo && numField("dossier_max_file_mb", a.ovMaxFile, 100, "admin-ov-maxfile-input")}
          </div>
        </div>
      )}
      <div>
        <label className="block font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">{a.note}</label>
        <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={500} rows={2}
          className="w-full rounded-md border border-slate-300 px-3.5 py-2.5 text-sm text-[#0F172A] outline-none focus:border-emerald-500 resize-none"
          data-testid="admin-note-input" />
      </div>
      <div className="flex gap-3 pt-1">
        <button type="button" onClick={() => setView("list")} className="flex-1 rounded-full border border-slate-300 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors" data-testid="admin-form-back">{a.back}</button>
        <button type="submit" disabled={saving} className="flex-1 rounded-full bg-emerald-500 py-3 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors disabled:opacity-60" data-testid="admin-form-submit">
          {saving ? a.creating : view === "create" ? a.create : a.save}
        </button>
      </div>
    </form>
  );

  const inner = (
    <>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2.5">
            {view !== "list" && view !== "password" && (
              <button onClick={() => setView("list")} className="text-slate-400 hover:text-[#0F172A]" data-testid="admin-back-btn"><CaretLeft size={18} /></button>
            )}
            <UserGear size={20} className="text-emerald-600" />
            <h3 className="font-display font-bold text-xl text-[#0F172A]">
              {view === "create" ? a.newClient : view === "edit" ? a.edit : view === "password" ? (created?.reset ? a.resetDone : a.created) : a.title}
            </h3>
          </div>
          <div className="flex items-center gap-3">
            {!embedded && view === "list" && (
              <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors" data-testid="admin-open-page">
                <ArrowSquareOut size={14} /> {a.openPage}
              </Link>
            )}
            {!embedded && (
              <button onClick={onClose} className="text-slate-400 hover:text-[#0F172A]" data-testid="admin-close"><X size={20} /></button>
            )}
          </div>
        </div>

        {view === "list" && (
          <div className="px-6 py-6">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-slate-500">{clients ? `${clients.length} ${a.clients.toLowerCase()}` : ""}</p>
              <button onClick={() => { setForm(emptyForm); setCreated(null); setView("create"); }}
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors" data-testid="admin-new-client-btn">
                <Plus size={16} weight="bold" /> {a.newClient}
              </button>
            </div>
            {!clients ? (
              <div className="flex justify-center py-12"><CircleNotch size={28} className="text-emerald-500 animate-spin" /></div>
            ) : clients.length === 0 ? (
              <p className="py-12 text-center text-sm text-slate-400" data-testid="admin-no-clients">{a.noClients}</p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="admin-client-list">
                {clients.map((c, i) => (
                  <li key={c.email} className="py-3.5 flex items-center gap-3" data-testid={`admin-client-row-${i}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono-tech text-sm font-semibold text-[#0F172A] truncate">{c.email}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">{badges(c)}</div>
                      <p className="mt-1 text-[11px] text-slate-400">
                        {c.machines} {a.machines} · {fmtGb(c.storage_used)} GB {a.storage}
                        {overrides(c) && <span className="text-indigo-500"> · {overrides(c)}</span>}
                      </p>
                    </div>
                    <button onClick={() => impersonate(c)} title={a.impersonate}
                      className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-2.5 text-indigo-600 hover:border-indigo-400 hover:bg-indigo-100 transition-colors shrink-0"
                      data-testid={`admin-client-impersonate-${i}`}><SignIn size={16} /><span className="hidden sm:inline text-xs font-semibold">{a.impersonateShort}</span></button>
                    <button onClick={() => openEdit(c)} title={a.edit}
                      className="inline-flex items-center justify-center rounded-full border border-slate-300 p-2.5 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors shrink-0"
                      data-testid={`admin-client-edit-${i}`}><PencilSimple size={16} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {(view === "create" || view === "edit") && formView}
        {view === "edit" && logsSection}

        {view === "password" && created && (
          <div className="px-6 py-8 text-center" data-testid="admin-password-view">
            <CheckCircle size={40} weight="fill" className="mx-auto text-emerald-500" />
            <p className="mt-4 font-display font-bold text-lg text-[#0F172A]">{created.email}</p>
            <p className="mt-4 font-mono-tech text-[11px] uppercase tracking-wider text-slate-500">{a.tempPwd}</p>
            <div className="mt-2 inline-flex items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-5 py-3">
              <code className="font-mono-tech text-lg font-bold text-emerald-800" data-testid="admin-temp-password">{created.temporary_password}</code>
              <button onClick={copyPwd} className="text-emerald-600 hover:text-emerald-800" title={a.copy} data-testid="admin-copy-password"><Copy size={18} /></button>
            </div>
            <p className="mt-4 max-w-md mx-auto text-sm text-slate-500">{a.pwdNote}</p>
            {!created.reset && <p className="mt-2 max-w-md mx-auto text-sm font-medium text-emerald-700" data-testid="admin-pwd-emailed">{a.pwdEmailed}</p>}
            <button onClick={() => setView("list")} className="mt-6 rounded-full bg-[#0A0F1A] px-6 py-3 text-sm font-semibold text-white hover:bg-[#0F172A] transition-colors" data-testid="admin-password-done">{a.back}</button>
          </div>
        )}
    </>
  );

  if (embedded) {
    return (
      <div className="w-full bg-white rounded-md border border-slate-200" data-testid="admin-panel">
        {inner}
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0A0F1A]/50 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }} transition={{ ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white rounded-md border border-slate-200 max-h-[90vh] overflow-y-auto" data-testid="admin-panel">
        {inner}
      </motion.div>
    </motion.div>
  );
};
